import * as Sentry from '@sentry/react'

import * as env from '@owl-nest/config'

const ENVIRONMENT = {
  DEVELOPMENT: 'development',
  PRODUCTION: 'production',
  SANDBOX: 'sandbox',
} as const

type Configuration = {
  dsn: string | undefined
  routerIntegration?: typeof reactRouterV6BrowserTracingIntegration
  environment: (typeof ENVIRONMENT)[keyof typeof ENVIRONMENT]
  user?: User
}

type ExtraData = {
  [id: string]: any
}

type User = Sentry.User & {
  country: string
  currency?: string
  isCompleted?: boolean
  locale: string
  timezone?: string
}

let DEBUG_MODE = false

/* CONFIGURATION */

export const getCurrentScope = Sentry.getCurrentScope
export const reactRouterV6BrowserTracingIntegration = Sentry.reactRouterV6BrowserTracingIntegration

export function init(configuration: Configuration): void {
  DEBUG_MODE = configuration.environment !== ENVIRONMENT.PRODUCTION

  if (!configuration.dsn) {
    console.warn('[logger] Warning: no DSN provided for Sentry')
    return
  }

  try {
    Sentry.init({
      denyUrls: [
        // Axeptio SDK
        '/https?:\\/\\/static.axept.io/',
        // Facebook
        /connect\.facebook\.net\/*\/all\.js/i,
        /graph\.facebook\.com/i,
        // Local stuff
        /^chrome:\/\//i,
        /extensions\//i,
        /^file:\/\//i,
      ],
      dsn: configuration.dsn,
      environment: configuration.environment,
      ignoreErrors: [
        // Generic non-error rejections
        'Object Not Found Matching Id',
        // Random browser extensions/plugins
        /avast/i,
        /^chrome:\/\//i,
        /chrome-extension:/i,
        /extensions\//i,
        /kaspersky/i,
        /safari-web-extension:/i,
        /safari-extension:/i,
        'top.GLOBALS',
        // We don't care about your local files
        /file:\/\//i,
        // Meta Platforms, Inc. stuff
        '_AutofillCallbackHandler',
        'fb_xd_fragment',
        // Apple, Safari and WebKit shenanigans
        /.*@webkit-masked-url.*/i, // See https://github.com/getsentry/sentry-javascript/discussions/5875
        'window.webkit.messageHandlers',
        'NPObject',
        '__gCrWeb', // See https://groups.google.com/a/chromium.org/forum/#!topic/chromium-discuss/7VU0_VvC7mE
        // Firefox
        'Permission denied to access property "toString"', // https://bugzilla.mozilla.org/show_bug.cgi?id=783260
        // Gogole* (*typo intended)
        /googlebot/i,
        // Android OS-level exceptions
        'androidInterface',
        'Java bridge method invocation error',
        'Java exception',
        'Java object is gone',
        // TikTok WebView thingies
        /.*this._perf.domInteractive.*/i,
        // Edge Mobile bugs
        /logMutedMessage/i,
        /wpmobileappStartLoading/i,
        /instantSearchSDKJSBridgeClearHighlight/i,
        // X
        'twttr is not defined',
      ],
      integrations: [
        Sentry.browserTracingIntegration(),
        configuration.routerIntegration ? configuration.routerIntegration : () => {},
      ],
      normalizeDepth: 10,
      release: env.GIT_SHA,
      tracesSampleRate: 1.0,
    })
  } catch (error) {
    console.warn("[logger] Warning: Sentry couldn't initialize with the given configuration", {
      dsn: configuration.dsn,
      environment: configuration.environment,
      error,
    })
    return
  }

  if (configuration.user) {
    Sentry.setTag('country', configuration.user.country)
    Sentry.setTag('currency', configuration.user.currency)
    Sentry.setTag('locale', configuration.user.locale)
  }

  // TODO: Remove this when restoring the original `whenGivenFor` instruction below.
  // Sentry.setTag('consent', consent.isGivenFor(consent.Vendor.SENTRY))
  // consent.whenGivenFor(consent.Vendor.SENTRY, {
  //   do: () => {
  //     Sentry.setTag('consent', true)
  //   },
  //   otherwise: () => {
  //     Sentry.setTag('consent', false)
  //   },
  // })

  // TODO: Restore when sh*t stops hitting the fan regarding the "Failed to fetch" errors.
  // consent.whenGivenFor(consent.Vendor.SENTRY, {
  //   do: () => {
  if (configuration.user) {
    setUser(configuration.user)
  }
  //   },
  //   otherwise: () => {
  //     /**
  //      * Sentry, as of version 5, doesn't allow clearing the user from context. Instead, they suggest using `clearContext()`
  //      * which is a bit too strong for our case. We avoid this by resetting the minimal fields manually.
  //      * Please feel free to update this once a better approach is available through the SDK.
  //      */
  //     setUser({
  //       isCompleted: undefined,
  //       locale: undefined,
  //       timezone: undefined,
  //     })
  //   },
  // })
}

export const setTag = Sentry.setTag

export function setUser(user: User): void {
  // if (consent.isGivenFor(consent.Vendor.SENTRY)) {
  Sentry.setUser(user)
  // }
}

/* MESSAGE LEVELS */

export function err(error: unknown, extra?: ExtraData, configureScope?: (scope: Sentry.Scope) => void): void {
  if (typeof error === 'string') {
    // Convert to Error object to force Sentry to keep stacktrace
    captureException(new Error(error), extra, configureScope)
  } else {
    captureException(error, extra, configureScope)
  }
}

export function inform(message: string, extra?: ExtraData, configureScope?: (scope: Sentry.Scope) => void): void {
  captureMessage(message, 'info', extra, configureScope)
}

export function log(message: string, extra?: ExtraData, configureScope?: (scope: Sentry.Scope) => void): void {
  captureMessage(message, 'log', extra, configureScope)
}

export function warn(message: string, extra?: ExtraData, configureScope?: (scope: Sentry.Scope) => void): void {
  captureMessage(message, 'warning', extra, configureScope)
}

function captureException(exception: any, extra?: ExtraData, configureScope?: (scope: Sentry.Scope) => void): void {
  Sentry.withScope((scope: Sentry.Scope) => {
    if (extra) {
      scope.setExtras(extra)
    }
    if (configureScope !== undefined) {
      configureScope(scope)
    }

    Sentry.captureException(exception)
  })

  if (DEBUG_MODE) print(exception, 'error', extra)
}

function captureMessage(
  message: string,
  severity: Sentry.SeverityLevel,
  extra?: ExtraData,
  configureScope?: (scope: Sentry.Scope) => void,
): void {
  Sentry.withScope((scope: Sentry.Scope) => {
    if (extra) {
      scope.setExtras(extra)
    }
    if (configureScope !== undefined) {
      configureScope(scope)
    }

    Sentry.captureMessage(message, severity)
  })

  if (DEBUG_MODE) print(message, severity, extra)
}

/* eslint-disable no-console */
function print(message: string, severity: Sentry.SeverityLevel, extra?: ExtraData): void {
  let printToConsole
  switch (severity) {
    case 'error':
    case 'fatal':
      printToConsole = console.error
      break
    case 'warning':
      printToConsole = console.warn
      break
    case 'info':
      printToConsole = console.info
      break
    case 'debug':
    case 'log':
    default:
      printToConsole = console.log
      break
  }

  function capitalize(s: string): string {
    if (!s) return ''
    return s[0].toUpperCase() + s.substring(1)
  }

  printToConsole(`${capitalize(severity)}: ${message}`, extra)
}
